import { ProjectCallsInsightType, ProjectCallsPatternType } from "@prisma/client";
import { TKey } from "@/app/translations/TKey";
import { CallAnalysisOverview, CallOverviewScoreKey } from "@/app/types/call/CallAnalysisOverview";
import { StandardCallType } from "@/app/types/call/StandardCallType";
import { getScoreLevel, ScoreLevel } from "../../CallDetail/components/CallScores/CallScores.logic";
import styles from "./AnalysisOverview.module.css";

export const OVERVIEW_SCORE_LABELS: Record<CallOverviewScoreKey, TKey> = {
  [CallOverviewScoreKey.PAIN]: TKey.CALL_OVERVIEW_SCORE_PAIN,
  [CallOverviewScoreKey.OBJECTION]: TKey.CALL_OVERVIEW_SCORE_OBJECTION,
  [CallOverviewScoreKey.BUYING_SIGNAL]: TKey.CALL_OVERVIEW_SCORE_BUYING_SIGNAL,
  [CallOverviewScoreKey.CONVERSATION_CLARITY]: TKey.CALL_OVERVIEW_SCORE_CONVERSATION_CLARITY,
  [CallOverviewScoreKey.PROSPECT_ENGAGEMENT]: TKey.CALL_OVERVIEW_SCORE_PROSPECT_ENGAGEMENT,
  [CallOverviewScoreKey.NEXT_STEP_CLARITY]: TKey.CALL_OVERVIEW_SCORE_NEXT_STEP_CLARITY,
};

export const CHART_AXIS_TICK = { className: styles.tick, fontSize: 12 };

export const CHART_AXIS_LINE = { className: styles.axisLine };

export const CHART_CURSOR = { className: styles.cursor };

export const CHART_LABEL_STYLE = { fill: "var(--color-grey-6)", fontSize: 12 };

export const TOOLTIP_SEPARATOR = " :";

export const CHART_TOOLTIP_STYLE = {
  background: "var(--color-white-1)",
  border: "1px solid var(--color-grey-2)",
  borderRadius: 8,
  fontSize: 12,
};

export function formatDuration(seconds: number): string {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);

  return hours > 0 ? `${hours} h ${String(minutes).padStart(2, "0")}` : `${minutes} min`;
}

export function formatDay(label: unknown): string {
  const [, month, day] = String(label).split("-");
  return day ? `${day}/${month}` : String(label);
}

export function scoreDelta(current: number | null, previous: number | null): number | null {
  if (current === null || previous === null) {
    return null;
  }

  return current - previous;
}

export type TopicColors = {
  color: string;
  bg: string;
};

const INSIGHT_COLORS: Record<ProjectCallsInsightType, TopicColors> = {
  [ProjectCallsInsightType.PAIN_POINT]: {
    color: "var(--color-analysis-pain)",
    bg: "var(--color-analysis-pain-bg)",
  },
  [ProjectCallsInsightType.OBJECTION]: {
    color: "var(--color-analysis-objection)",
    bg: "var(--color-analysis-objection-bg)",
  },
  [ProjectCallsInsightType.BUYING_SIGNAL]: {
    color: "var(--color-analysis-signal)",
    bg: "var(--color-analysis-signal-bg)",
  },
  [ProjectCallsInsightType.VERBATIM]: {
    color: "var(--color-analysis-verbatim)",
    bg: "var(--color-analysis-verbatim-bg)",
  },
  [ProjectCallsInsightType.FEATURE_REQUEST]: {
    color: "var(--color-analysis-feature)",
    bg: "var(--color-analysis-feature-bg)",
  },
  [ProjectCallsInsightType.COMPETITOR_MENTION]: {
    color: "var(--color-analysis-competitor)",
    bg: "var(--color-analysis-competitor-bg)",
  },
};

const PATTERN_COLORS: Record<ProjectCallsPatternType, TopicColors> = {
  [ProjectCallsPatternType.TALK_BALANCE]: {
    color: "var(--color-pattern-balance)",
    bg: "var(--color-pattern-balance-bg)",
  },
  [ProjectCallsPatternType.PROSPECT_ENGAGEMENT]: {
    color: "var(--color-pattern-engagement)",
    bg: "var(--color-pattern-engagement-bg)",
  },
  [ProjectCallsPatternType.SPEECH_CLARITY]: {
    color: "var(--color-pattern-speech)",
    bg: "var(--color-pattern-speech-bg)",
  },
  [ProjectCallsPatternType.NEXT_STEPS]: {
    color: "var(--color-pattern-next-steps)",
    bg: "var(--color-pattern-next-steps-bg)",
  },
  [ProjectCallsPatternType.FRICTION_MOMENTS]: {
    color: "var(--color-pattern-friction)",
    bg: "var(--color-pattern-friction-bg)",
  },
  [ProjectCallsPatternType.EXCHANGE_TRENDS]: {
    color: "var(--color-pattern-trends)",
    bg: "var(--color-pattern-trends-bg)",
  },
};

const FALLBACK_COLORS: TopicColors = {
  color: "var(--color-green-3)",
  bg: "var(--color-green-4)",
};

const INSIGHT_TYPE_KEYS: Record<ProjectCallsInsightType, TKey> = {
  [ProjectCallsInsightType.PAIN_POINT]: TKey.CALL_ANALYSIS_INSIGHT_PAIN,
  [ProjectCallsInsightType.OBJECTION]: TKey.CALL_ANALYSIS_INSIGHT_OBJECTION,
  [ProjectCallsInsightType.BUYING_SIGNAL]: TKey.CALL_ANALYSIS_INSIGHT_SIGNALS,
  [ProjectCallsInsightType.VERBATIM]: TKey.CALL_ANALYSIS_INSIGHT_VERBATIMS,
  [ProjectCallsInsightType.FEATURE_REQUEST]: TKey.CALL_ANALYSIS_INSIGHT_FEATURES,
  [ProjectCallsInsightType.COMPETITOR_MENTION]: TKey.CALL_ANALYSIS_INSIGHT_COMPETITORS,
};

const PATTERN_TYPE_KEYS: Record<ProjectCallsPatternType, TKey> = {
  [ProjectCallsPatternType.TALK_BALANCE]: TKey.CALL_ANALYSIS_PATTERN_TALK_BALANCE,
  [ProjectCallsPatternType.PROSPECT_ENGAGEMENT]: TKey.CALL_ANALYSIS_PATTERN_PROSPECT_ENGAGEMENT,
  [ProjectCallsPatternType.SPEECH_CLARITY]: TKey.CALL_ANALYSIS_PATTERN_SPEECH_CLARITY,
  [ProjectCallsPatternType.NEXT_STEPS]: TKey.CALL_ANALYSIS_PATTERN_NEXT_STEPS,
  [ProjectCallsPatternType.FRICTION_MOMENTS]: TKey.CALL_ANALYSIS_PATTERN_FRICTION_MOMENTS,
  [ProjectCallsPatternType.EXCHANGE_TRENDS]: TKey.CALL_ANALYSIS_PATTERN_EXCHANGE_TRENDS,
};

export function topicTypeKey(type: string): TKey | undefined {
  return (
    INSIGHT_TYPE_KEYS[type as ProjectCallsInsightType] ??
    PATTERN_TYPE_KEYS[type as ProjectCallsPatternType]
  );
}

export function topicColors(type: string): TopicColors {
  return (
    INSIGHT_COLORS[type as ProjectCallsInsightType] ??
    PATTERN_COLORS[type as ProjectCallsPatternType] ??
    FALLBACK_COLORS
  );
}

const SCORE_LEVEL_FILLS: Record<ScoreLevel, string> = {
  [ScoreLevel.VERY_HIGH]: styles.fillGood,
  [ScoreLevel.HIGH]: styles.fillGood,
  [ScoreLevel.MEDIUM]: styles.fillMedium,
  [ScoreLevel.LOW]: styles.fillLow,
  [ScoreLevel.VERY_LOW]: styles.fillLow,
};

const SCORE_LEVEL_COLORS: Record<ScoreLevel, string> = {
  [ScoreLevel.VERY_HIGH]: "var(--color-green-3)",
  [ScoreLevel.HIGH]: "var(--color-green-3)",
  [ScoreLevel.MEDIUM]: "var(--color-analysis-objection)",
  [ScoreLevel.LOW]: "var(--color-analysis-pain)",
  [ScoreLevel.VERY_LOW]: "var(--color-analysis-pain)",
};

export function scoreFill(value: number): string {
  return SCORE_LEVEL_FILLS[getScoreLevel(value)];
}

export function scoreColor(value: number): string {
  return SCORE_LEVEL_COLORS[getScoreLevel(value)];
}

const CALL_TYPE_FILLS: Record<StandardCallType, string> = {
  [StandardCallType.DISCOVERY]: styles.fillTypeDiscovery,
  [StandardCallType.DEMO]: styles.fillTypeDemo,
  [StandardCallType.INTERVIEW]: styles.fillTypeInterview,
  [StandardCallType.CLOSING]: styles.fillTypeClosing,
};

const CALL_TYPE_COLORS: Record<StandardCallType, string> = {
  [StandardCallType.DISCOVERY]: "var(--color-type-discovery)",
  [StandardCallType.DEMO]: "var(--color-type-demo)",
  [StandardCallType.INTERVIEW]: "var(--color-type-interview)",
  [StandardCallType.CLOSING]: "var(--color-type-closing)",
};

export function isStandardCallType(type: string): boolean {
  return (Object.values(StandardCallType) as string[]).includes(type);
}

export function typeFill(type: string): string {
  return CALL_TYPE_FILLS[type as StandardCallType] ?? styles.fillTypeOther;
}

export function typeColor(type: string): string {
  return CALL_TYPE_COLORS[type as StandardCallType] ?? "var(--color-grey-1)";
}

export const OVERVIEW_PLACEHOLDER: CallAnalysisOverview = {
  analysedCalls: 12,
  totalCalls: 14,
  durationSeconds: 15600,
  globalScore: 72,
  previousGlobalScore: 66,
  avgQuestions: 9,
  timeline: [
    { label: "2026-01-05", score: 58, calls: 2 },
    { label: "2026-01-12", score: 64, calls: 3 },
    { label: "2026-01-19", score: 69, calls: 2 },
    { label: "2026-01-26", score: 74, calls: 3 },
    { label: "2026-02-02", score: 79, calls: 2 },
  ],
  scores: [
    { key: CallOverviewScoreKey.PAIN, value: 74 },
    { key: CallOverviewScoreKey.OBJECTION, value: 58 },
    { key: CallOverviewScoreKey.BUYING_SIGNAL, value: 66 },
    { key: CallOverviewScoreKey.CONVERSATION_CLARITY, value: 81 },
    { key: CallOverviewScoreKey.PROSPECT_ENGAGEMENT, value: 63 },
    { key: CallOverviewScoreKey.NEXT_STEP_CLARITY, value: 45 },
  ],
  types: [
    { type: StandardCallType.DISCOVERY, calls: 6 },
    { type: StandardCallType.DEMO, calls: 4 },
    { type: StandardCallType.CLOSING, calls: 2 },
  ],
  members: [],
  topInsights: [
    { label: "Budget non validé en amont", type: "OBJECTION", level: null, calls: 8 },
    { label: "Process manuel chronophage", type: "PAIN_POINT", level: null, calls: 7 },
    { label: "Demande une intégration CRM", type: "FEATURE_REQUEST", level: null, calls: 5 },
    { label: "Compare avec un concurrent", type: "COMPETITOR_MENTION", level: null, calls: 4 },
  ],
  topPatterns: [
    { label: "Temps de parole déséquilibré", type: "TALK_BALANCE", level: null, calls: 9 },
    { label: "Prochaine étape non posée", type: "NEXT_STEPS", level: null, calls: 6 },
    { label: "Questions de découverte trop rares", type: "SPEECH_CLARITY", level: null, calls: 5 },
  ],
};
